import React from 'react';
import { Table, Tag, Typography, Modal, Toast } from '@douyinfe/semi-ui';
import { renderTimestamp, stringToColor } from '../../helpers/render';

const { Paragraph } = Typography;

const LogsList = ({ logs, loading, pageSize, setPageSize, showRMB, dollarCost, serverPage, serverTotal, serverPageSize, onServerPageChange }) => {

    const copyText = async (text) => {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            Toast.success('已复制：' + text);
        } else {
            Modal.info({ title: '复制内容', content: text });
        }
    };

    const columns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            width: '11%',
            render: (text) => (
                <span style={{ fontFamily: 'var(--code-font, monospace)', color: 'var(--apple-text-secondary)', fontSize: 12 }}>
                    {renderTimestamp(text)}
                </span>
            ),
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            width: '13%',
            ellipsis: true,
            render: (text) => (
                <Tag
                    color={stringToColor(text)}
                    onClick={() => copyText(text)}
                    style={{
                        cursor: 'pointer',
                        borderRadius: 6,
                        fontWeight: 600,
                        border: 'none',
                        padding: '3px 6px',
                        fontSize: 12,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                    }}
                >
                    {text}
                </Tag>
            ),
        },
        {
            title: 'IP',
            dataIndex: 'ip',
            width: '9%',
            render: (text) => (
                <Tag style={{
                    background: 'rgba(52, 199, 89, 0.1)',
                    color: 'var(--apple-green)',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                }}>
                    {text || '未知'}
                </Tag>
            )
        },
        {
            title: '用时',
            dataIndex: 'use_time',
            width: '5%',
            render: (text) => (
                <Tag style={{ background: 'rgba(0,0,0,0.04)', color: '#666', border: 'none', borderRadius: 6, fontSize: 12 }}>
                    {text}s
                </Tag>
            )
        },
        {
            title: '输入',
            dataIndex: 'prompt_tokens',
            width: '15%',
            render: (text, record) => {
                if (record.model_name.startsWith('mj_')) return null;
                if (record.type !== 0 && record.type !== 2) return null;

                let cacheRead = 0;
                let cacheWrite = 0;
                let totalInput = parseInt(text) || 0;
                try {
                    const other = typeof record.other === 'string' ? JSON.parse(record.other) : record.other;
                    if (other) {
                        cacheRead = other.cache_tokens || 0;
                        const c5m = other.cache_creation_tokens_5m || 0;
                        const c1h = other.cache_creation_tokens_1h || 0;
                        const cBase = other.cache_creation_tokens || 0;
                        cacheWrite = (c5m > 0 || c1h > 0) ? c5m + c1h : cBase;
                        // 如果主记录 prompt_tokens=0 但 usage 里有真实值，用 usage 的
                        if (totalInput === 0 && other.usage && other.usage.prompt_tokens > 0) {
                            totalInput = other.usage.prompt_tokens;
                        }
                    }
                } catch (e) { /* ignore */ }

                const hasCacheRead = cacheRead > 0;
                const hasCacheWrite = cacheWrite > 0;
                const hasCache = hasCacheRead || hasCacheWrite;
                // 普通输入 = 总输入 - 缓存读 - 缓存写
                const baseInput = Math.max(0, totalInput - cacheRead - cacheWrite);
                const displayInput = hasCache ? baseInput : totalInput;

                return (
                    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3 }}>
                        <span style={{ fontFamily: 'var(--code-font, monospace)' }}>
                            {displayInput.toLocaleString()}
                        </span>
                        {hasCache && (
                            <span style={{
                                marginTop: 2,
                                fontSize: 11,
                                color: 'var(--apple-text-secondary, #86868b)',
                                fontFamily: 'var(--code-font, monospace)',
                                whiteSpace: 'nowrap',
                            }}>
                                {hasCacheRead && hasCacheWrite
                                    ? `缓存读 ${cacheRead.toLocaleString()} · 写 ${cacheWrite.toLocaleString()}`
                                    : hasCacheRead
                                        ? `缓存读 ${cacheRead.toLocaleString()}`
                                        : `缓存写 ${cacheWrite.toLocaleString()}`}
                            </span>
                        )}
                    </div>
                );
            },
            sorter: (a, b) => (a.prompt_tokens || 0) - (b.prompt_tokens || 0)
        },
        {
            title: '补全',
            dataIndex: 'completion_tokens',
            width: '6%',
            render: (text, record) => {
                if (parseInt(text) > 0 && (record.type === 0 || record.type === 2)) {
                    return <span style={{ fontFamily: 'var(--code-font, monospace)' }}>{text}</span>;
                }
                return null;
            },
            sorter: (a, b) => (a.completion_tokens || 0) - (b.completion_tokens || 0)
        },
        {
            title: '花费',
            dataIndex: 'quota',
            width: '7%',
            render: (text) => {
                const usdValue = typeof text === 'number' ? text : 0;
                if (showRMB) {
                    const rate = parseFloat(dollarCost || 0);
                    return (
                        <span style={{ fontWeight: 600, color: 'var(--apple-text-primary)' }}>
                            ￥{(usdValue * rate).toFixed(4)}
                        </span>
                    );
                }
                return (
                    <span style={{ fontWeight: 600, color: 'var(--apple-text-primary)' }}>
                        ${usdValue.toFixed(6)}
                    </span>
                );
            }
        },
        {
            title: '详情',
            dataIndex: 'content',
            width: '34%',
            render: (text, record) => {
                // 错误日志(type=5)也需要把错误原因展示给用户，否则详情列会为空。
                if (record.type === 5) {
                    const displayText = text || '';
                    if (!displayText) return null;

                    return (
                        <Paragraph
                            style={{ color: 'var(--apple-red, #ff3b30)' }}
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {displayText}
                        </Paragraph>
                    );
                }

                if (record.type !== 0 && record.type !== 2) {
                    return <></>;
                }

                // 统一从 other 解析计费详情
                let detailNode = null;
                try {
                    const other = typeof record.other === 'string' ? JSON.parse(record.other) : record.other;
                    if (other) {
                        const modelRatio = other.model_ratio;
                        const modelPrice = other.model_price;
                        const completionRatio = other.completion_ratio;
                        const groupRatio = other.group_ratio;
                        const cacheTokens = other.cache_tokens || 0;
                        const cacheRatio = other.cache_ratio || 0;
                        const c5m = other.cache_creation_tokens_5m || 0;
                        const c1h = other.cache_creation_tokens_1h || 0;
                        const cBase = other.cache_creation_tokens || 0;
                        const cacheCreationTokens = (c5m > 0 || c1h > 0) ? c5m + c1h : cBase;
                        const cacheCreationRatio = other.cache_creation_ratio || 0;
                        const hasCache = cacheTokens > 0 || cacheCreationTokens > 0;

                        // 从 usage 或主记录取真实 token
                        const usage = other.usage || {};
                        const promptTokens = usage.prompt_tokens || record.prompt_tokens || 0;
                        const completionTokens = usage.completion_tokens || record.completion_tokens || 0;

                        const lines = [];
                        if (modelPrice !== undefined && modelPrice >= 0 && modelPrice !== -1) {
                            lines.push(`模型价格 $${modelPrice}`);
                        } else if (modelRatio !== undefined && modelRatio >= 0) {
                            // 1 ratio = $0.002/1K = $2/1M
                            const inputPrice = modelRatio * 2.0;
                            lines.push(`输入 $${inputPrice.toFixed(2)}/1M`);
                            if (completionRatio) {
                                lines.push(`输出 $${(inputPrice * completionRatio).toFixed(2)}/1M`);
                            }
                            if (hasCache && cacheRatio > 0) {
                                lines.push(`缓存读 $${(inputPrice * cacheRatio).toFixed(2)}/1M`);
                            }
                            if (hasCache && cacheCreationRatio > 0) {
                                lines.push(`缓存写 $${(inputPrice * cacheCreationRatio).toFixed(2)}/1M`);
                            }
                        }
                        if (groupRatio !== undefined && groupRatio !== 1) {
                            lines.push(`分组倍率 ${groupRatio}`);
                        }

                        if (lines.length > 0) {
                            detailNode = lines.join('，');
                        }
                    }
                } catch (e) { /* ignore */ }

                // fallback 到 content 文本
                if (!detailNode) {
                    let displayText = text || '';
                    if (displayText) {
                        const match = displayText.match(/(模型倍率|模型价格).*/);
                        displayText = match ? match[0] : displayText;
                        displayText = displayText.replace(/(\d+)\.00/g, '$1');
                    }
                    detailNode = displayText;
                }

                if (!detailNode) return null;

                return (
                    <Paragraph
                        ellipsis={{
                            rows: 2,
                            showTooltip: {
                                type: 'popover',
                            },
                        }}
                    >
                        {detailNode}
                    </Paragraph>
                );
            }
        }
    ];

    // 服务端分页模式
    const useServerPaging = serverTotal > 0 && onServerPageChange;
    const totalPages = useServerPaging ? Math.ceil(serverTotal / serverPageSize) : 0;

    return (
        <div className="apple-card" style={{ marginTop: '24px', overflow: 'visible' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>
                    调用日志
                    {serverTotal > 0 && (
                        <span style={{ fontSize: '13px', fontWeight: 'normal', color: 'var(--apple-text-secondary, #86868b)', marginLeft: '8px' }}>
                            共 {serverTotal} 条{useServerPaging ? `，第 ${serverPage}/${totalPages} 页` : ''}
                        </span>
                    )}
                    {loading && (
                        <span style={{ fontSize: '13px', fontWeight: 'normal', color: 'var(--apple-text-secondary, #86868b)', marginLeft: '8px' }}>
                            加载中…
                        </span>
                    )}
                </h3>
            </div>
            <div className="logs-table-scroll">
                <Table
                    columns={columns}
                    dataSource={logs}
                    loading={loading}
                    size="small"
                    style={{ width: '100%' }}
                    pagination={useServerPaging ? {
                        currentPage: serverPage,
                        total: serverTotal,
                        pageSize: serverPageSize,
                        onPageChange: onServerPageChange,
                        showTotal: true,
                        formatPageText: ({ currentStart, currentEnd, total }) =>
                            `第 ${currentStart} - ${currentEnd} 条，共 ${total} 条`,
                    } : {
                        pageSize: pageSize,
                        onPageSizeChange: setPageSize,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`
                    }}
                    className="glass-table proportional-log-table"
                />
            </div>
        </div>
    );
};

export default LogsList;

